import { supabase } from '@/integrations/supabase/client';

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  factor?: number;
}

const defaultOptions: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  factor: 2
};

/**
 * Exponential backoff delay function
 */
const calculateDelay = (attempt: number, options: Required<RetryOptions>): number => {
  const delay = options.baseDelay * Math.pow(options.factor, attempt);
  return Math.min(delay, options.maxDelay);
};

/**
 * Sleep function for delays
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Retry wrapper for Supabase operations with exponential backoff
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const config = { ...defaultOptions, ...options };
  let lastError: any;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on authentication errors or permission errors
      if (error?.message?.includes('JWT') || 
          error?.message?.includes('permission') ||
          error?.message?.includes('unauthorized') ||
          error?.code === 'PGRST301') {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === config.maxRetries) {
        break;
      }

      // Calculate delay for next attempt
      const delay = calculateDelay(attempt, config);
      console.warn(`API call failed (attempt ${attempt + 1}/${config.maxRetries + 1}), retrying in ${delay}ms...`, error);
      
      await sleep(delay);
    }
  }

  throw lastError;
};

/**
 * Wrapper for Supabase queries with retry logic
 */
export const supabaseQuery = async <T = any>(
  queryBuilder: any,
  options: RetryOptions = {}
): Promise<{ data: T; error: any }> => {
  return withRetry(async () => {
    const { data, error } = await queryBuilder;
    
    if (error) {
      throw error;
    }
    
    return { data, error: null };
  }, options);
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  return error?.message?.includes('Failed to fetch') ||
         error?.message?.includes('NetworkError') ||
         error?.message?.includes('fetch') ||
         error?.name === 'TypeError';
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: any): string => {
  if (isNetworkError(error)) {
    return 'Problème de connexion. Vérifiez votre internet et réessayez.';
  }
  
  if (error?.message?.includes('JWT')) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  
  if (error?.message?.includes('permission')) {
    return 'Accès non autorisé à cette ressource.';
  }
  
  return error?.message || 'Une erreur inattendue est survenue.';
};